import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, Collapse, Empty, Modal, Popconfirm, Segmented, Select, Space, Spin,
  Switch, Table, Tag, Tooltip, InputNumber, message,
} from 'antd';
import {
  AuditOutlined, BugOutlined, CheckCircleOutlined, CloseCircleOutlined, DatabaseOutlined,
  ExperimentOutlined, FileTextOutlined, FilePdfOutlined, MinusCircleOutlined,
  OrderedListOutlined, PictureOutlined, PlayCircleOutlined, ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { uiTestAPI, testDocAPI } from '../services/api';
import { usePermission } from '../contexts/PermissionContext';
import { useFitTableHeight } from '../hooks/useViewportFit';
import { brand, gray, font } from '../theme/tokens';
import PlanTab from './testdocs/PlanTab';
import CaseSpecTab from './testdocs/CaseSpecTab';
import DatasetTab from './testdocs/DatasetTab';
import DefectTab from './testdocs/DefectTab';
import { downloadBlob } from './testdocs/common';

/* ────────────────────────────────────────────────────────────
   테스트결과 — Playwright 화면테스트 실행 이력 · 커버리지

   상단 요약은 '액션이 나오는 축'으로 잡는다 (CLAUDE.md 상단 요약 축 원칙):
   통과/실패 비율보다 **무엇이 깨졌고 어떤 화면이 안 돌았는지**가 담당자가 할 일이다.
   ──────────────────────────────────────────────────────────── */

interface RunRow {
  id: number; run_key: string; trigger_type: 'auto' | 'manual';
  status: string; targets: string;
  total_cases: number; passed_cases: number; failed_cases: number; skipped_cases: number;
  pass_rate: number; screens_total: number; screens_covered: number; coverage_pct: number;
  duration_ms: number; runner_version: string; error_message: string;
  requested_by: string; started_at: string | null; finished_at: string | null; created_at: string;
  plan_id: number | null; has_data_snapshot: boolean;
  approved_by: string; approved_at: string | null;
}

interface CaseRow {
  id: number; site: string; screen_code: string; screen_name: string; screen_path: string;
  check_code: string; check_name: string; status: 'passed' | 'failed' | 'skipped';
  duration_ms: number; detail: string; has_shot: boolean;
}

interface ScreenRow {
  site: string; code: string; name: string; group: string; path: string; critical: boolean;
  declared_checks: number; executed: number; passed: number; failed: number; skipped: number;
  covered: boolean; status: 'passed' | 'failed' | 'uncovered'; cases: CaseRow[];
}

const RUN_STATUS: Record<string, { label: string; color: string; bg: string }> = {
  passed: { label: '통과', color: '#047857', bg: '#d1fae5' },
  failed: { label: '실패', color: '#b91c1c', bg: '#fee2e2' },
  error: { label: '오류', color: '#9a3412', bg: '#ffedd5' },
  timeout: { label: '미응답', color: '#a16207', bg: '#fef3c7' },
  running: { label: '실행 중', color: '#1d4ed8', bg: '#dbeafe' },
  pending: { label: '대기', color: '#475569', bg: '#e2e8f0' },
};

const CASE_STATUS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  passed: { label: '통과', color: '#10b981', icon: <CheckCircleOutlined /> },
  failed: { label: '실패', color: '#ef4444', icon: <CloseCircleOutlined /> },
  skipped: { label: '건너뜀', color: '#94a3b8', icon: <MinusCircleOutlined /> },
};

const SITE_META: Record<string, { label: string; color: string }> = {
  lab: { label: 'lab', color: '#4f46e5' },
  partner: { label: 'partner', color: '#f59e0b' },
};

const fmtDuration = (ms: number) => (ms >= 60000
  ? `${Math.floor(ms / 60000)}분 ${Math.round((ms % 60000) / 1000)}초`
  : `${(ms / 1000).toFixed(1)}초`);

/** 캡처 미리보기 — 권한이 붙은 엔드포인트라 blob 으로 받아 그린다 */
const ShotView: React.FC<{ load: () => Promise<any>; alt: string }> = ({ load, alt }) => {
  const [url, setUrl] = useState<string>('');
  const [state, setState] = useState<'loading' | 'ok' | 'none'>('loading');
  useEffect(() => {
    let revoked = '';
    load()
      .then((res: any) => { revoked = URL.createObjectURL(res.data); setUrl(revoked); setState('ok'); })
      .catch(() => setState('none'));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (state === 'loading') return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
  if (state === 'none') {
    return (
      <Empty description="캡처가 없습니다 (자동 실행은 실패 화면만 캡처를 남깁니다)"
        image={Empty.PRESENTED_IMAGE_SIMPLE} />
    );
  }
  return <img src={url} alt={alt} style={{ width: '100%', border: `1px solid ${gray[200]}`, borderRadius: 8 }} />;
};

const TestResultPage: React.FC = () => {
  const { hasPermission } = usePermission();
  const canWrite = hasPermission('ui_tests', 'WRITE');
  const canDelete = hasPermission('ui_tests', 'DELETE');

  const [tab, setTab] = useState<'runs' | 'coverage' | 'plan' | 'cases' | 'data' | 'defects'>('runs');
  const [summary, setSummary] = useState<any>(null);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [screens, setScreens] = useState<ScreenRow[]>([]);
  const [detail, setDetail] = useState<{ run: RunRow; cases: CaseRow[]; data_snapshot?: any } | null>(null);
  const [shot, setShot] = useState<{ title: string; load: () => Promise<any> } | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  const [requesting, setRequesting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();
  const pollRef = useRef<any>(null);

  const loadSummary = useCallback(async () => {
    try {
      const res = await uiTestAPI.summary();
      setSummary(res.data);
      return res.data;
    } catch { return null; }
  }, []);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await uiTestAPI.list({ status: statusFilter, page, page_size: pageSize });
      setRuns(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '실행 이력을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, pageSize]);

  const loadCoverage = useCallback(async () => {
    try {
      const res = await uiTestAPI.coverage();
      setScreens(res.data.screens || []);
    } catch { /* 권한 없으면 조용히 */ }
  }, []);

  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => { loadSummary(); loadCoverage(); }, [loadSummary, loadCoverage]);

  /* 실행 대기/진행 중에는 폴링 — 워처가 1분 주기라 결과가 곧 들어온다 */
  useEffect(() => {
    const active = summary?.active;
    if (!active) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const s = await loadSummary();
      if (!s?.active) {
        clearInterval(pollRef.current); pollRef.current = null;
        loadRuns(); loadCoverage();
        message.success('화면테스트 실행이 끝났습니다.');
      }
    }, 10000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [summary?.active, loadSummary, loadRuns, loadCoverage]);

  const runNow = async () => {
    setRequesting(true);
    try {
      const res = await uiTestAPI.request();
      if (res.data.queued) {
        message.success('실행을 요청했습니다. 1분 안에 시작됩니다.');
      } else {
        message.info(res.data.reason || '이미 실행 중입니다.');
      }
      loadSummary(); loadRuns();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '실행 요청에 실패했습니다.');
    } finally {
      setRequesting(false);
    }
  };

  const openSettings = async () => {
    try {
      const res = await uiTestAPI.getSettings();
      setSettings(res.data);
      setSettingsOpen(true);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '설정을 불러오지 못했습니다.');
    }
  };

  const saveSettings = async () => {
    try {
      await uiTestAPI.updateSettings({
        enabled: settings.enabled, targets: settings.targets,
        keep_days: settings.keep_days, notify_level: settings.notify_level,
      });
      message.success('설정이 저장되었습니다.');
      setSettingsOpen(false);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '설정 저장에 실패했습니다.');
    }
  };

  const downloadReport = async (r: RunRow) => {
    try {
      const res = await uiTestAPI.reportPdf(r.id);
      downloadBlob(res, `uitest_report_${r.id}.pdf`);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '결과보고서를 만들지 못했습니다.');
    }
  };

  /** 결과보고서 확인(승인) — 실행 상태(passed/failed)는 바꾸지 않는다. 사실과 판단은 분리한다. */
  const approveRun = async (id: number) => {
    try {
      const res = await uiTestAPI.approve(id);
      message.success('결과보고서를 확인 처리했습니다.');
      setDetail((d: any) => (d ? { ...d, run: res.data.run } : d));
      loadRuns();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '확인 처리에 실패했습니다.');
    }
  };

  /** 실패 케이스 → 결함대장 등록 (자동 등록분이 이미 있으면 그것을 알려준다) */
  const registerDefect = async (c: CaseRow) => {
    try {
      const res = await testDocAPI.defectFromCase(c.id);
      message.success(res.data.created
        ? `결함 ${res.data.defect.defect_no} 으로 등록되었습니다.`
        : `이미 등록된 결함입니다 (${res.data.defect.defect_no}, ${res.data.defect.occurrence_count}회 발생).`);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '결함 등록에 실패했습니다.');
    }
  };

  const openDetail = async (id: number) => {
    try {
      const res = await uiTestAPI.detail(id);
      setDetail(res.data);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '상세를 불러오지 못했습니다.');
    }
  };

  const latest = summary?.latest as RunRow | undefined;
  const failures = useMemo(() => (summary?.failures || []) as CaseRow[], [summary]);
  const uncovered = useMemo(() => (summary?.uncovered || []) as any[], [summary]);
  const coverageOk = latest && latest.screens_total > 0 && latest.screens_covered >= latest.screens_total;

  /* ── 상단 요약: 커버리지 스택바 + 실패/미커버 칩 ── */
  const heroBar = useMemo(() => {
    if (!latest) return null;
    const t = latest.screens_total || 1;
    const failedScreens = new Set(failures.map((f) => `${f.site}:${f.screen_code}`)).size;
    const okScreens = Math.max(0, latest.screens_covered - failedScreens);
    const segs = [
      { key: 'ok', label: '정상', n: okScreens, color: '#10b981' },
      { key: 'fail', label: '실패 있음', n: failedScreens, color: '#ef4444' },
      { key: 'none', label: '미커버', n: Math.max(0, t - latest.screens_covered), color: '#cbd5e1' },
    ].filter((s) => s.n > 0);
    return (
      <div style={{ display: 'flex', gap: 2, height: 14, borderRadius: 7, overflow: 'hidden', background: '#f1f5f9' }}>
        {segs.map((s) => (
          <Tooltip key={s.key} title={`${s.label} ${s.n}개 화면`}>
            <div style={{ width: `${(s.n / t) * 100}%`, background: s.color }} />
          </Tooltip>
        ))}
      </div>
    );
  }, [latest, failures]);

  const runColumns = [
    {
      title: '실행', dataIndex: 'run_key', width: 190,
      render: (v: string, r: RunRow) => (
        <Space size={6}>
          <Tag color={r.trigger_type === 'auto' ? 'blue' : 'purple'} style={{ margin: 0 }}>
            {r.trigger_type === 'auto' ? '자동' : '수동'}
          </Tag>
          <span style={{ fontFamily: font.mono, fontSize: 12 }}>{v}</span>
        </Space>
      ),
    },
    {
      /* 보고서 확인 여부는 별도 열로 두지 않는다 — 열을 더하면 표가 컨테이너보다 넓어져
         가로 스크롤 밖으로 밀리고, 정작 확인 여부가 기본 화면에서 안 보인다.
         상태 태그 옆 작은 체크가 어느 폭에서도 보인다 (2026-08-30 캡처 검토) */
      title: '결과', dataIndex: 'status', width: 108,
      render: (v: string, r: RunRow) => {
        const m = RUN_STATUS[v] || RUN_STATUS.pending;
        return (
          <Space size={4}>
            <Tag style={{ margin: 0, color: m.color, background: m.bg, borderColor: m.bg, fontWeight: 700 }}>{m.label}</Tag>
            {r.approved_at && (
              <Tooltip title={`보고서 확인 · ${r.approved_by} · ${r.approved_at}`}>
                <CheckCircleOutlined style={{ color: '#047857', fontSize: 14 }} />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: '케이스 (통과/실패/건너뜀)', width: 210,
      render: (_: any, r: RunRow) => (
        r.total_cases === 0 ? <span style={{ color: gray[400] }}>—</span> : (
          <Space size={4}>
            <span style={{ fontWeight: 700 }}>{r.total_cases}</span>
            <span style={{ color: gray[400] }}>·</span>
            <span style={{ color: '#10b981', fontWeight: 600 }}>{r.passed_cases}</span>
            <span style={{ color: gray[300] }}>/</span>
            <span style={{ color: r.failed_cases ? '#ef4444' : gray[400], fontWeight: 600 }}>{r.failed_cases}</span>
            <span style={{ color: gray[300] }}>/</span>
            <span style={{ color: gray[400] }}>{r.skipped_cases}</span>
          </Space>
        )
      ),
    },
    {
      title: '화면 커버리지', width: 150,
      render: (_: any, r: RunRow) => {
        const full = r.screens_total > 0 && r.screens_covered >= r.screens_total;
        return r.screens_total === 0 ? <span style={{ color: gray[400] }}>—</span> : (
          <Space size={6}>
            <span style={{ fontWeight: 700, color: full ? '#047857' : '#b45309' }}>
              {r.coverage_pct}%
            </span>
            <span style={{ color: gray[400], fontSize: 12 }}>
              {r.screens_covered}/{r.screens_total}
            </span>
          </Space>
        );
      },
    },
    { title: '대상', dataIndex: 'targets', width: 130,
      render: (v: string) => (
        <Space size={4}>
          {(v || '').split(',').filter(Boolean).map((s) => (
            <Tag key={s} style={{ margin: 0, color: SITE_META[s]?.color, borderColor: SITE_META[s]?.color }}>
              {SITE_META[s]?.label || s}
            </Tag>
          ))}
        </Space>
      ) },
    { title: '소요', dataIndex: 'duration_ms', width: 90,
      render: (v: number) => (v ? <span style={{ fontFamily: font.mono, fontSize: 12 }}>{fmtDuration(v)}</span> : '—') },
    { title: '요청자', dataIndex: 'requested_by', width: 100, ellipsis: true },
    { title: '시각', dataIndex: 'created_at', width: 155,
      render: (v: string) => <span style={{ fontSize: 12, color: gray[500] }}>{v}</span> },
    {
      title: '', width: 165, fixed: 'right' as const,
      render: (_: any, r: RunRow) => (
        <Space size={4}>
          <Button size="small" onClick={() => openDetail(r.id)}
            disabled={r.status === 'pending'}>상세</Button>
          <Tooltip title="회차별 결과보고서 PDF">
            <Button size="small" icon={<FilePdfOutlined />}
              disabled={r.status === 'pending' || r.status === 'running'}
              onClick={() => downloadReport(r)} />
          </Tooltip>
          {canDelete && (
            <Popconfirm title="이 실행 기록을 삭제할까요?" okText="삭제" cancelText="취소"
              onConfirm={async () => {
                try { await uiTestAPI.remove(r.id); message.success('삭제되었습니다.'); loadRuns(); loadSummary(); }
                catch (e: any) { message.error(e?.response?.data?.detail || '삭제에 실패했습니다.'); }
              }}>
              <Button size="small" danger type="text">삭제</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const screenColumns = [
    { title: '사이트', dataIndex: 'site', width: 90,
      render: (v: string) => (
        <Tag style={{ margin: 0, color: SITE_META[v]?.color, borderColor: SITE_META[v]?.color }}>
          {SITE_META[v]?.label || v}
        </Tag>
      ) },
    { title: '화면', dataIndex: 'name', width: 190,
      render: (v: string, r: ScreenRow) => (
        <Space size={6}>
          <span style={{ fontWeight: 600 }}>{v}</span>
          {r.critical && <Tag color="red" style={{ margin: 0, fontSize: 11 }}>핵심</Tag>}
        </Space>
      ) },
    { title: '그룹', dataIndex: 'group', width: 120,
      render: (v: string) => <span style={{ color: gray[500], fontSize: 12 }}>{v}</span> },
    { title: '경로', dataIndex: 'path', width: 160,
      render: (v: string) => <span style={{ fontFamily: font.mono, fontSize: 12, color: gray[500] }}>{v}</span> },
    { title: '상태', dataIndex: 'status', width: 100,
      render: (v: string) => {
        if (v === 'uncovered') return <Tag color="default" style={{ margin: 0, fontWeight: 700 }}>미커버</Tag>;
        if (v === 'failed') return <Tag color="error" style={{ margin: 0, fontWeight: 700 }}>실패</Tag>;
        return <Tag color="success" style={{ margin: 0, fontWeight: 700 }}>통과</Tag>;
      } },
    { title: '검사 (실행/선언)', width: 130,
      render: (_: any, r: ScreenRow) => (
        <Space size={4}>
          <span style={{ fontWeight: 700, color: r.executed >= r.declared_checks ? '#047857' : '#b45309' }}>
            {r.executed}
          </span>
          <span style={{ color: gray[300] }}>/</span>
          <span style={{ color: gray[500] }}>{r.declared_checks}</span>
          {r.skipped > 0 && <span style={{ color: gray[400], fontSize: 11 }}>(건너뜀 {r.skipped})</span>}
        </Space>
      ) },
    { title: '실패 검사', width: 260,
      render: (_: any, r: ScreenRow) => {
        const f = r.cases.filter((c) => c.status === 'failed');
        if (!f.length) return <span style={{ color: gray[300] }}>—</span>;
        return (
          <Space size={4} wrap>
            {f.map((c) => (
              <Tooltip key={c.id} title={c.detail}>
                <Tag color="error" style={{ margin: 0, cursor: 'help' }}>{c.check_name}</Tag>
              </Tooltip>
            ))}
          </Space>
        );
      } },
    { title: '', width: 90, fixed: 'right' as const,
      render: (_: any, r: ScreenRow) => (
        <Button size="small" icon={<PictureOutlined />}
          onClick={() => setShot({
            title: `${SITE_META[r.site]?.label || r.site} · ${r.name}`,
            load: () => uiTestAPI.screenShot(r.site, r.code),
          })}>캡처</Button>
      ) },
  ];

  return (
    <div ref={cardWrapRef}>
      {/* ── 상단 요약 ── */}
      <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: '14px 18px' } }}>
        {!latest ? (
          <Space>
            <ExperimentOutlined style={{ color: brand.primary }} />
            <span style={{ color: gray[500] }}>아직 실행 기록이 없습니다. 매일 06:40에 자동 실행되며, 지금 실행할 수도 있습니다.</span>
          </Space>
        ) : (
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 210 }}>
              <div style={{ fontSize: 12, color: gray[500], marginBottom: 2 }}>
                최근 실행 · {latest.run_key} ({latest.trigger_type === 'auto' ? '자동' : '수동'})
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 30, fontWeight: 800, letterSpacing: -1,
                  color: latest.status === 'passed' ? '#047857' : '#b91c1c' }}>
                  {latest.coverage_pct}%
                </span>
                <span style={{ fontSize: 13, color: gray[500] }}>
                  화면 커버리지 ({latest.screens_covered}/{latest.screens_total})
                </span>
              </div>
              <div style={{ marginTop: 8, width: 210 }}>{heroBar}</div>
            </div>

            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ fontSize: 12, color: gray[500], marginBottom: 6 }}>
                케이스 {latest.total_cases}건 · 통과 {latest.passed_cases} · 실패 {latest.failed_cases} · 건너뜀 {latest.skipped_cases}
                {latest.duration_ms ? ` · ${fmtDuration(latest.duration_ms)}` : ''}
              </div>
              <Space size={6} wrap>
                {!coverageOk && (
                  <Tag color="default" style={{ fontWeight: 700 }}>
                    미커버 화면 {uncovered.length}개
                  </Tag>
                )}
                {failures.length === 0 && coverageOk && (
                  <Tag color="success" style={{ fontWeight: 700 }}>
                    <CheckCircleOutlined /> 전 화면 정상
                  </Tag>
                )}
                {failures.slice(0, 8).map((f) => (
                  <Tooltip key={f.id} title={f.detail}>
                    <Tag color="error" style={{ cursor: 'help' }}>
                      {SITE_META[f.site]?.label}·{f.screen_name} · {f.check_name}
                    </Tag>
                  </Tooltip>
                ))}
                {failures.length > 8 && <Tag>외 {failures.length - 8}건</Tag>}
              </Space>
            </div>

            <Space>
              {summary?.active && (
                <Tag color="processing" style={{ fontWeight: 700 }}>
                  <Spin size="small" style={{ marginRight: 6 }} />
                  {summary.active.status === 'running' ? '실행 중' : '대기 중'}
                </Tag>
              )}
              {canWrite && (
                <Button type="primary" icon={<PlayCircleOutlined />} loading={requesting}
                  disabled={!!summary?.active} onClick={runNow}>지금 실행</Button>
              )}
              {canWrite && <Button icon={<SettingOutlined />} onClick={openSettings}>자동 실행</Button>}
              <Button icon={<ReloadOutlined />} onClick={() => { loadRuns(); loadSummary(); loadCoverage(); }} />
            </Space>
          </div>
        )}
        {latest?.error_message && (
          <Alert type="warning" showIcon style={{ marginTop: 10 }}
            message={latest.error_message} />
        )}
      </Card>

      <Card size="small" styles={{ body: { padding: 12 } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <Segmented
            value={tab}
            onChange={(v) => setTab(v as any)}
            options={[
              { label: '실행 이력', value: 'runs', icon: <ExperimentOutlined /> },
              { label: '화면 커버리지', value: 'coverage', icon: <OrderedListOutlined /> },
              { label: '테스트계획서', value: 'plan', icon: <FileTextOutlined /> },
              { label: '케이스 명세', value: 'cases', icon: <AuditOutlined /> },
              { label: '테스트 데이터', value: 'data', icon: <DatabaseOutlined /> },
              { label: '결함관리', value: 'defects', icon: <BugOutlined /> },
            ]}
          />
          {tab === 'runs' && (
            <Select
              allowClear placeholder="결과 필터" style={{ width: 140 }}
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(1); }}
              options={Object.entries(RUN_STATUS).map(([k, v]) => ({ value: k, label: v.label }))}
            />
          )}
          {(tab === 'runs' || tab === 'coverage') && (
            <span style={{ marginLeft: 'auto', fontSize: 12, color: gray[400] }}>
              읽기·조회만 검사합니다 (등록/수정/삭제는 실행하지 않음)
            </span>
          )}
        </div>

        <div ref={tableWrapRef} className="fit-table">
          {tab === 'plan' ? (
            <PlanTab bodyHeight={bodyHeight!} canWrite={canWrite} canDelete={canDelete} />
          ) : tab === 'cases' ? (
            <CaseSpecTab bodyHeight={bodyHeight!} canWrite={canWrite} />
          ) : tab === 'data' ? (
            <DatasetTab bodyHeight={bodyHeight!} canWrite={canWrite} canDelete={canDelete} />
          ) : tab === 'defects' ? (
            <DefectTab bodyHeight={bodyHeight!} canWrite={canWrite} canDelete={canDelete} />
          ) : tab === 'runs' ? (
            <Table<RunRow>
              rowKey="id" size="small" loading={loading}
              dataSource={runs} columns={runColumns as any}
              /* x 는 선언 폭 합계와 맞춘다 — 작게 잡으면 antd 가 열을 압축해 fixed:right
                 액션열이 마지막 열을 덮고, 크게 잡으면 불필요한 가로 스크롤이 생긴다 */
              scroll={{ y: bodyHeight, x: 1306 }}
              rowClassName={(_, i) => (i % 2 ? 'table-row-alt' : '')}
              pagination={{
                current: page, pageSize, total, showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100', '200'],
                showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`,
                onChange: (p, ps) => { setPage(ps !== pageSize ? 1 : p); setPageSize(ps); },
                size: 'small',
              }}
            />
          ) : (
            <Table<ScreenRow>
              rowKey={(r) => `${r.site}:${r.code}`} size="small"
              dataSource={screens} columns={screenColumns as any}
              scroll={{ y: bodyHeight, x: 1250 }}
              rowClassName={(r, i) => `${i % 2 ? 'table-row-alt' : ''}`}
              pagination={{
                pageSize: 20, showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100', '200'],
                showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}개 화면`,
                size: 'small',
              }}
            />
          )}
        </div>
      </Card>

      {/* ── 실행 상세 ── */}
      <Modal
        open={!!detail} onCancel={() => setDetail(null)} footer={null} width={1000}
        title={detail && (
          <Space>
            <span>화면테스트 #{detail.run.id}</span>
            <Tag color={detail.run.trigger_type === 'auto' ? 'blue' : 'purple'}>
              {detail.run.trigger_type === 'auto' ? '자동' : '수동'}
            </Tag>
            <span style={{ fontSize: 13, color: gray[500], fontWeight: 400 }}>{detail.run.run_key}</span>
          </Space>
        )}
      >
        {detail && (
          <>
            <Space size={16} style={{ marginBottom: 10 }} wrap>
              <span>커버리지 <b>{detail.run.coverage_pct}%</b> ({detail.run.screens_covered}/{detail.run.screens_total})</span>
              <span>통과 <b style={{ color: '#10b981' }}>{detail.run.passed_cases}</b></span>
              <span>실패 <b style={{ color: detail.run.failed_cases ? '#ef4444' : gray[400] }}>{detail.run.failed_cases}</b></span>
              <span>건너뜀 <b style={{ color: gray[400] }}>{detail.run.skipped_cases}</b></span>
              <span style={{ color: gray[500] }}>{fmtDuration(detail.run.duration_ms)} · {detail.run.runner_version}</span>
            </Space>

            <Space size={8} style={{ marginBottom: 12 }} wrap>
              <Button icon={<FilePdfOutlined />} onClick={() => downloadReport(detail.run)}>
                결과보고서 PDF
              </Button>
              {canWrite && (detail.run.approved_at
                ? <Tag color="success" style={{ fontWeight: 700, padding: '4px 10px' }}>
                    <CheckCircleOutlined /> {detail.run.approved_by} 확인 · {detail.run.approved_at}
                  </Tag>
                : <Popconfirm title="이 회차 결과보고서를 확인 처리할까요?" okText="확인" cancelText="취소"
                    onConfirm={() => approveRun(detail.run.id)}>
                    <Button type="primary" ghost icon={<CheckCircleOutlined />}>보고서 확인</Button>
                  </Popconfirm>)}
              {!detail.run.has_data_snapshot && (
                <span style={{ fontSize: 12, color: gray[400] }}>
                  이 실행에는 데이터 스냅샷이 없습니다 (러너 구버전)
                </span>
              )}
            </Space>

            {/* 사용한 테스트 데이터 — '무엇으로 검증했는가'가 없으면 통과의 의미가 흐려진다 */}
            {detail.data_snapshot && (
              <Collapse size="small" style={{ marginBottom: 12 }} items={[{
                key: 'data',
                label: <span style={{ fontSize: 13, fontWeight: 600 }}>
                  <DatabaseOutlined /> 사용한 테스트 데이터
                </span>,
                children: (
                  <div style={{ fontSize: 12 }}>
                    {(detail.data_snapshot.accounts || []).map((a: any, i: number) => (
                      <div key={`a${i}`} style={{ marginBottom: 3 }}>
                        <b>계정 ({a.site})</b> {a.username}
                        <span style={{ color: gray[400] }}> · 출처 {a.source} (비밀번호는 보관하지 않음)</span>
                      </div>
                    ))}
                    {(detail.data_snapshot.dynamic || []).map((d: any, i: number) => (
                      <div key={`d${i}`} style={{ marginBottom: 3 }}>
                        <b>동적 선택 ({d.site})</b> {d.key} = {String(d.value)}
                        <span style={{ color: gray[400] }}> · {d.source}</span>
                      </div>
                    ))}
                    {detail.data_snapshot.env && (
                      <div style={{ marginBottom: 3 }}>
                        <b>실행 환경</b>{' '}
                        <span style={{ fontFamily: font.mono, fontSize: 11.5 }}>
                          {detail.data_snapshot.env.viewport} · {detail.data_snapshot.env.browser} ·
                          {' '}헤드리스 {String(detail.data_snapshot.env.headless)}
                        </span>
                      </div>
                    )}
                    {!!(detail.data_snapshot.observed || []).length && (
                      <div style={{ marginTop: 6 }}>
                        <b>관측 데이터량 ({detail.data_snapshot.observed.length}건)</b>
                        <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {detail.data_snapshot.observed.map((o: any, i: number) => (
                            <Tag key={`o${i}`} style={{ margin: 0, fontSize: 11 }}>
                              {o.site}:{o.screen_code} {o.metric}={o.value}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ),
              }]} />
            )}
            <Table<CaseRow>
              rowKey="id" size="small" dataSource={detail.cases}
              scroll={{ y: 420 }} pagination={false}
              rowClassName={(r) => (r.status === 'failed' ? '' : '')}
              columns={[
                { title: '', dataIndex: 'status', width: 40,
                  render: (v: string) => {
                    const m = CASE_STATUS[v] || CASE_STATUS.skipped;
                    return <Tooltip title={m.label}><span style={{ color: m.color, fontSize: 15 }}>{m.icon}</span></Tooltip>;
                  } },
                { title: '사이트', dataIndex: 'site', width: 80,
                  render: (v: string) => (
                    <Tag style={{ margin: 0, color: SITE_META[v]?.color, borderColor: SITE_META[v]?.color }}>
                      {SITE_META[v]?.label || v}
                    </Tag>
                  ) },
                { title: '화면', dataIndex: 'screen_name', width: 150 },
                { title: '검사', dataIndex: 'check_name', width: 170 },
                { title: '내용', dataIndex: 'detail', ellipsis: true,
                  render: (v: string) => (
                    <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{v}</span>}>
                      <span style={{ fontSize: 12, color: gray[600] }}>{(v || '').split('\n')[0]}</span>
                    </Tooltip>
                  ) },
                { title: '소요', dataIndex: 'duration_ms', width: 80,
                  render: (v: number) => <span style={{ fontFamily: font.mono, fontSize: 11, color: gray[500] }}>{v}ms</span> },
                { title: '', width: 140,
                  render: (_: any, r: CaseRow) => (
                    <Space size={2}>
                      {r.has_shot && (
                        <Button size="small" type="link" icon={<PictureOutlined />}
                          onClick={() => setShot({ title: `${r.screen_name} · ${r.check_name}`, load: () => uiTestAPI.caseShot(r.id) })}>
                          캡처
                        </Button>
                      )}
                      {r.status === 'failed' && canWrite && (
                        <Tooltip title="결함관리대장에 등록 (자동 등록분이 있으면 알려줍니다)">
                          <Button size="small" type="link" icon={<BugOutlined />}
                            onClick={() => registerDefect(r)}>결함</Button>
                        </Tooltip>
                      )}
                    </Space>
                  ) },
              ] as any}
            />
          </>
        )}
      </Modal>

      {/* ── 캡처 미리보기 ── */}
      <Modal open={!!shot} onCancel={() => setShot(null)} footer={null} width={1180}
        title={shot?.title}>
        {shot && <ShotView load={shot.load} alt={shot.title} />}
      </Modal>

      {/* ── 자동 실행 설정 ── */}
      <Modal open={settingsOpen} onCancel={() => setSettingsOpen(false)} onOk={saveSettings}
        okText="저장" cancelText="취소" width={520}
        title={<Space><SettingOutlined />자동 실행 설정</Space>}>
        {settings && (
          <Space direction="vertical" size={16} style={{ width: '100%', marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 110, color: gray[600] }}>매일 자동 실행</span>
              <Switch checked={settings.enabled}
                onChange={(v) => setSettings({ ...settings, enabled: v })} />
              <span style={{ fontSize: 12, color: gray[400] }}>매일 06:40 KST (크론이 트리거)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 110, color: gray[600] }}>대상 사이트</span>
              <Select mode="multiple" style={{ flex: 1 }}
                value={(settings.targets || '').split(',').filter(Boolean)}
                onChange={(v: string[]) => setSettings({ ...settings, targets: v.join(',') })}
                options={[{ value: 'lab', label: 'lab (관리자)' }, { value: 'partner', label: 'partner (파트너 포털)' }]}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 110, color: gray[600] }}>이력 보관</span>
              <InputNumber min={7} max={365} value={settings.keep_days}
                onChange={(v) => setSettings({ ...settings, keep_days: v })} addonAfter="일" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 110, color: gray[600] }}>결과 통지</span>
              <Select style={{ flex: 1 }} value={settings.notify_level}
                onChange={(v) => setSettings({ ...settings, notify_level: v })}
                options={[
                  { value: 'failed', label: '실패했을 때만 (권장)' },
                  { value: 'always', label: '매번' },
                  { value: 'never', label: '보내지 않음' },
                ]} />
            </div>
            <Alert type="info" showIcon
              message="테스트는 읽기·조회·모달·필터까지만 수행합니다."
              description="운영 DB를 쓰므로 등록·수정·삭제는 검사 대상에서 제외했습니다. 결과 통지는 Discord와 알림센터로 함께 나갑니다." />
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default TestResultPage;
